import { Injectable, NgZone, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import type { Messaging } from 'firebase/messaging';
import { API_EVALUATION_URL, FIREBASE_CONFIG, FIREBASE_VAPID_KEY } from '../constants/app.constants';
import { ThongBaoService } from './thong-bao.service';

const SW_URL = '/firebase-messaging-sw.js';
/** Token FCM của trình duyệt này, giữ lại để xoá khỏi backend khi đăng xuất. */
const KHOA_TOKEN = 'fcm_web_token';
/** Đăng xuất không được chờ lâu vì mạng/Firebase chậm. */
const THOI_GIAN_CHO_HUY_MS = 3000;
/** Tiêu đề push của API gửi thử (FirebaseTokenResource.testWebPush). */
const TIEU_DE_THONG_BAO_THU = '[Thông báo thử]';

/** Kết quả `POST /firebase-tokens/web/test` (FirebaseService.KetQuaGuiThu ở backend). */
export interface KetQuaGuiThuPush {
  /** Số token theo loại: `WEB`, `APP_KINH_DOANH`. */
  soToken: Record<string, number>;
  tongToken: number;
  soGuiThanhCong: number;
  /** Số token chết backend vừa tự xoá (UNREGISTERED, SENDER_ID_MISMATCH). */
  soTokenDaXoa: number;
  loi: Array<{ loaiToken?: string; token?: string; maLoi?: string; moTa?: string }>;
  /** Trình duyệt hiện tại có token đã đăng ký thành công với backend hay không. */
  trinhDuyetDaDangKy: boolean;
}

/**
 * Web push FCM: backend gửi push APP_KINH_DOANH thì gửi kèm token loại WEB của cùng người dùng.
 * SDK Firebase được import động để không làm tăng initial bundle.
 */
@Injectable({ providedIn: 'root' })
export class WebPushService {
  private http = inject(HttpClient);
  private zone = inject(NgZone);
  private notification = inject(NzNotificationService);
  private thongBaoService = inject(ThongBaoService);

  private readonly url = `${API_EVALUATION_URL}/firebase-tokens/web`;
  private messaging?: Messaging;
  private dangKyPromise?: Promise<void>;

  /** Trình duyệt hỗ trợ web push và trang chạy trên HTTPS/localhost. */
  async hoTro(): Promise<boolean> {
    if (typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator)) {
      return false;
    }
    try {
      const { isSupported } = await import('firebase/messaging');
      return await isSupported();
    } catch {
      return false;
    }
  }

  quyenHienTai(): NotificationPermission | 'unsupported' {
    return typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported';
  }

  /** Gọi từ thao tác bấm của người dùng: trình duyệt chỉ cho xin quyền khi có gesture. */
  async xinQuyenVaDangKy(): Promise<boolean> {
    if (!(await this.hoTro())) return false;
    const quyen = await Notification.requestPermission();
    if (quyen !== 'granted') return false;
    await this.dangKy();
    return true;
  }

  /** Đã được cấp quyền thì đăng ký lại token (mỗi lần tải trang, sau khi đăng nhập). Không ném lỗi. */
  async dangKyNeuDaCoQuyen(): Promise<void> {
    if (this.quyenHienTai() !== 'granted' || !(await this.hoTro())) return;
    await this.dangKy();
  }

  /**
   * Xoá token web push khi phiên đăng nhập kết thúc. Không bao giờ chặn đăng xuất.
   *
   * - Xoá khỏi backend (cần Bearer token còn hiệu lực) khi {@code goiBackend} = true - tức đăng xuất chủ
   *   động. Phiên đã hết hạn thì bỏ qua bước này vì backend sẽ từ chối.
   * - Luôn huỷ token ở phía Firebase (không cần JWT): token cũ thành không hợp lệ nên trình duyệt này
   *   không còn nhận push của người vừa đăng xuất, kể cả khi bước backend lỗi. Làm cả khi localStorage
   *   không còn giữ token, hoặc Firebase chưa được khởi tạo ở lần tải trang này.
   */
  async huyDangKy(goiBackend = true): Promise<void> {
    const token = this.docToken();
    this.xoaTokenLuu();
    const huy = (async () => {
      if (goiBackend && token) {
        try {
          await firstValueFrom(this.http.delete(this.url, { params: new HttpParams().set('token', token) }));
        } catch {
          // Bỏ qua: token Firebase bị huỷ ngay dưới đây nên backend gửi tới cũng không còn tới trình duyệt.
        }
      }
      try {
        if (this.messaging || (this.quyenHienTai() === 'granted' && (await this.hoTro()))) {
          const messaging = await this.layMessaging();
          const { deleteToken } = await import('firebase/messaging');
          await deleteToken(messaging);
        }
      } catch {
        // Bỏ qua
      }
    })();
    await Promise.race([huy, new Promise<void>(r => setTimeout(r, THOI_GIAN_CHO_HUY_MS))]);
  }

  /**
   * Chẩn đoán (chỉ dùng ở dev): đăng ký lại token của trình duyệt này nếu đã có quyền, rồi nhờ backend
   * gửi push thử tới mọi token của chính người đang đăng nhập.
   */
  async guiThongBaoThu(): Promise<KetQuaGuiThuPush> {
    await this.dangKyNeuDaCoQuyen();
    const res: any = await firstValueFrom(this.http.post(`${this.url}/test`, {}));
    if (res && res.status !== undefined && res.status !== 0) {
      throw new Error(res.description || 'Gửi thông báo thử thất bại');
    }
    const payload = res?.payLoadContent ?? res?.payload;
    const data = payload && payload.data !== undefined ? payload.data : payload;
    return {
      soToken: data?.soToken ?? {},
      tongToken: Number(data?.tongToken) || 0,
      soGuiThanhCong: Number(data?.soGuiThanhCong) || 0,
      soTokenDaXoa: Number(data?.soTokenDaXoa) || 0,
      loi: Array.isArray(data?.loi) ? data.loi : [],
      trinhDuyetDaDangKy: !!this.docToken()
    };
  }

  /**
   * Chẩn đoán (dev): hiện thông báo hệ thống trực tiếp qua service worker, KHÔNG đi qua FCM. Không hiện
   * popup Windows ở bước này nghĩa là bị chặn ở Windows/Chrome, không phải lỗi backend/Firebase.
   */
  async thuPopupHeThong(): Promise<string> {
    if (this.quyenHienTai() !== 'granted') {
      return `Quyền trình duyệt đang là "${this.quyenHienTai()}" - chưa cho phép thông báo.`;
    }
    const registration = await navigator.serviceWorker.getRegistration('/');
    if (!registration) {
      return 'Chưa có service worker firebase-messaging-sw.js - trình duyệt chưa đăng ký web push.';
    }
    await registration.showNotification('[Thử popup Windows] TND Valuation', {
      body: 'Thông báo hiện trực tiếp từ service worker, không qua FCM.',
      tag: 'tnd-thu-popup'
    });
    return 'Đã yêu cầu Chrome hiện popup. Không thấy popup/không có trong Notification Center → '
      + 'bị chặn ở Windows (Notifications, Do not disturb, Focus) hoặc chrome://flags/#enable-system-notifications.';
  }

  private dangKy(): Promise<void> {
    // Gộp các lần gọi đồng thời (vd bấm bật quyền đúng lúc component khởi tạo).
    if (!this.dangKyPromise) {
      this.dangKyPromise = this.thucHienDangKy().finally(() => (this.dangKyPromise = undefined));
    }
    return this.dangKyPromise;
  }

  private async thucHienDangKy(): Promise<void> {
    try {
      const messaging = await this.layMessaging();
      const { getToken } = await import('firebase/messaging');
      const registration = await navigator.serviceWorker.register(SW_URL);
      const token = await getToken(messaging, {
        vapidKey: FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration
      });
      if (!token) return;
      // Luôn gửi lại: backend gán token cho người đang đăng nhập, kể cả khi người trước hết phiên mà không đăng xuất.
      const res: any = await firstValueFrom(this.http.post(this.url, { token }));
      if (res && res.status !== undefined && res.status !== 0) {
        console.warn('Đăng ký thông báo trình duyệt thất bại:', res.description);
        return;
      }
      this.luuToken(token);
    } catch (err) {
      console.warn('Không bật được thông báo trình duyệt', err);
    }
  }

  private async layMessaging(): Promise<Messaging> {
    if (this.messaging) return this.messaging;
    const [{ initializeApp, getApps }, { getMessaging, onMessage }] = await Promise.all([
      import('firebase/app'),
      import('firebase/messaging')
    ]);
    const app = getApps()[0] ?? initializeApp(FIREBASE_CONFIG);
    const messaging = getMessaging(app);
    // Tab đang hiển thị: Firebase KHÔNG đưa push ra thông báo hệ thống (Windows) mà chuyển cho trang -
    // tự hiện toast và cập nhật badge chuông. Tab ẩn/đóng thì service worker hiện thông báo hệ thống.
    onMessage(messaging, payload => {
      this.zone.run(() => {
        const title = payload.notification?.title || 'Thông báo mới';
        const body = payload.notification?.body || payload.data?.['message'] || '';
        const laThongBaoThu = title.startsWith(TIEU_DE_THONG_BAO_THU);
        this.notification.info(title,
          laThongBaoThu ? `${body}\n(Nhận trong trang vì tab đang hiển thị - Windows sẽ không hiện popup.)` : body,
          laThongBaoThu ? { nzDuration: 0, nzStyle: { whiteSpace: 'pre-line' } } : undefined);
        this.thongBaoService.capNhatSoChuaDoc();
      });
    });
    this.messaging = messaging;
    return messaging;
  }

  private docToken(): string | null {
    try {
      return localStorage.getItem(KHOA_TOKEN);
    } catch {
      return null;
    }
  }

  private luuToken(token: string): void {
    try {
      localStorage.setItem(KHOA_TOKEN, token);
    } catch {
      // Bỏ qua
    }
  }

  private xoaTokenLuu(): void {
    try {
      localStorage.removeItem(KHOA_TOKEN);
    } catch {
      // Bỏ qua
    }
  }
}
